import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import api, { BASE_URL, fotoUrl } from '../api';
import { useTheme } from '../contexts/ThemeContext';

// ─── Tipler ───────────────────────────────────────────────────────────────────
interface Egitmen {
  id: string; ad: string; soyad: string; email: string; tel: string;
  uzmanlik: string[]; bio: string; hakedis_oran: number; foto_url: string | null;
}
interface Seans { gun: string; baslangic_saat: string; bitis_saat: string; }
interface Ders {
  id: string; ad: string; seviye: string; salon_adi: string;
  kapasite: number; kayitli_ogrenci: number; hakedis_oran: number; seanslar: Seans[];
}
interface Ogrenci {
  id: string; ad: string; soyad: string; foto_url: string | null;
  kalan_hak: number; birim_fiyat: number; onceki_durum: string | null;
}
interface DersDetayOgrenci {
  id: string; ad: string; soyad: string; email: string;
  foto_url: string | null; kalan_hak: number | null; donduruldu: boolean;
}
interface YoklamaGecmis {
  id: string; tarih: string; egitmen_notu: string;
  katildi_sayisi: number; gelmedi_sayisi: number; toplam_hakedis: number;
}
interface Hakedis { toplam_birikmis: number; toplam_odenen: number; }

// ─── Sabitler ─────────────────────────────────────────────────────────────────
const GUNLER: { kisalt: string; tam: string; js: number }[] = [
  { kisalt: 'Pzt', tam: 'Pazartesi', js: 1 }, { kisalt: 'Sal', tam: 'Salı',      js: 2 },
  { kisalt: 'Çar', tam: 'Çarşamba',  js: 3 }, { kisalt: 'Per', tam: 'Perşembe',  js: 4 },
  { kisalt: 'Cum', tam: 'Cuma',      js: 5 }, { kisalt: 'Cmt', tam: 'Cumartesi', js: 6 },
  { kisalt: 'Paz', tam: 'Pazar',     js: 0 },
];
const GUN_MAP = Object.fromEntries(GUNLER.map(g => [g.js, g.kisalt]));
const SAAT = (s: string) => s?.slice(0, 5) ?? '';
const TARIH_STR = (d: Date) => d.toISOString().slice(0, 10);
const BUGUN = TARIH_STR(new Date());
const TR_TARIH = (s: string) => new Date(s).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });

const SEV_RENK: Record<string, [string, string]> = {
  'Başlangıç': ['#E6F1FB', '#0C447C'], 'Orta': ['#FAEEDA', '#854F0B'], 'İleri': ['#EAF3DE', '#3B6D11'],
};

const RENK_PALETI: [string, string][] = [
  ['#EEEDFE', '#534AB7'], ['#E6F1FB', '#0C447C'], ['#EAF3DE', '#3B6D11'],
  ['#FAEEDA', '#854F0B'], ['#FCEBEB', '#A32D2D'], ['#F0F9FF', '#0369A1'],
  ['#F5F0FF', '#7C3AED'], ['#FFF7ED', '#C2410C'],
];
const saatToMin = (s: string) => { const [h, m] = s.split(':').map(Number); return h * 60 + m; };
const GRID_BAS = 8 * 60;
const GRID_BIT = 22 * 60;
const SAAT_YUK = 56;

type Sekme = 'profil' | 'dersler' | 'program' | 'yoklama';

// ─── Ana bileşen ──────────────────────────────────────────────────────────────
export default function EgitmenPaneli() {
  const { kullanici } = useAuth();
  const id = kullanici?.id ?? '';
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';

  const [sekme, setSekme] = useState<Sekme>('profil');
  const [egitmen, setEgitmen] = useState<Egitmen | null>(null);
  const [dersler, setDersler] = useState<Ders[]>([]);
  const [hakedis, setHakedis] = useState<Hakedis>({ toplam_birikmis: 0, toplam_odenen: 0 });
  const [yukleniyor, setYukleniyor] = useState(true);

  // Ders detay drawer
  const [secilenDersId, setSecilenDersId] = useState<string | null>(null);
  const [dersDetay, setDersDetay] = useState<{ ders: any; ogrenciler: DersDetayOgrenci[] } | null>(null);
  const [dersDetayYuk, setDersDetayYuk] = useState(false);

  // Profil düzenleme
  const [duzenleModal, setDuzenleModal] = useState(false);
  const [duzenleForm, setDuzenleForm] = useState({ tel: '', uzmanlik: '', bio: '' });
  const [fotoYukleniyor, setFotoYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [formHata, setFormHata] = useState('');

  const yukle = async () => {
    if (!id) return;
    setYukleniyor(true);
    try {
      const [eRes, dRes, hRes] = await Promise.all([
        api.get(`/api/egitmenler/${id}`),
        api.get(`/api/egitmenler/${id}/dersler`),
        api.get(`/api/hakedis/${id}`),
      ]);
      setEgitmen(eRes.data);
      setDersler(dRes.data);
      setHakedis(hRes.data.ozet);
    } catch (e) { console.error(e); }
    finally { setYukleniyor(false); }
  };

  useEffect(() => { yukle(); }, [id]);

  useEffect(() => {
    if (!secilenDersId) { setDersDetay(null); return; }
    setDersDetayYuk(true);
    api.get(`/api/dersler/${secilenDersId}`)
      .then(r => setDersDetay({ ders: r.data.ders, ogrenciler: r.data.ogrenciler ?? [] }))
      .catch(console.error)
      .finally(() => setDersDetayYuk(false));
  }, [secilenDersId]);

  const fotoYukle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFotoYukleniyor(true);
    const fd = new FormData(); fd.append('foto', file);
    try { await api.post(`/api/egitmenler/${id}/foto`, fd); yukle(); }
    catch { alert('Fotoğraf yüklenemedi.'); }
    finally { setFotoYukleniyor(false); }
  };

  const duzenleAc = () => {
    if (!egitmen) return;
    setDuzenleForm({ tel: egitmen.tel ?? '', uzmanlik: (egitmen.uzmanlik ?? []).join(', '), bio: egitmen.bio ?? '' });
    setFormHata(''); setDuzenleModal(true);
  };

  const duzenleKaydet = async () => {
    setKaydediliyor(true); setFormHata('');
    try {
      const uzmanlik = duzenleForm.uzmanlik.split(',').map(s => s.trim()).filter(Boolean);
      await api.patch(`/api/egitmenler/${id}/kisisel`, { tel: duzenleForm.tel, uzmanlik, bio: duzenleForm.bio });
      setDuzenleModal(false); yukle();
    } catch (e: any) { setFormHata(e.response?.data?.hata ?? 'Hata oluştu'); }
    finally { setKaydediliyor(false); }
  };

  const inputStDynamic: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: 13, boxSizing: 'border-box',
    background: card, color: isDark ? '#e8e8e8' : undefined,
  };
  const btnStDynamic: React.CSSProperties = {
    padding: '8px 16px', borderRadius: 6, border: `1px solid ${isDark ? '#333' : '#ddd'}`, background: card, cursor: 'pointer', fontSize: 13, color: isDark ? '#e8e8e8' : undefined,
  };

  if (yukleniyor) return <Spinner />;
  if (!egitmen) return <div style={{ padding: 24, color: textMuted }}>Profil bulunamadı.</div>;

  const bekleyen = parseFloat(String(hakedis?.toplam_birikmis ?? 0)) - parseFloat(String(hakedis?.toplam_odenen ?? 0));

  return (
    <div style={{ padding: '24px 24px 48px' }}>

      {/* Sekmeler */}
      <div style={{ display: 'flex', gap: 4, background: card, borderRadius: 10, padding: 4, marginBottom: 20, border: `1px solid ${border}` }}>
        {([
          { key: 'profil',   label: 'Profilim',   badge: 0 },
          { key: 'dersler',  label: 'Derslerim',  badge: dersler.length },
          { key: 'program',  label: 'Program',    badge: 0 },
          { key: 'yoklama',  label: 'Yoklama Al', badge: 0 },
        ] as const).map(s => (
          <button key={s.key} onClick={() => setSekme(s.key)} style={{
            flex: 1, padding: '9px 8px', borderRadius: 7, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: sekme === s.key ? 600 : 400,
            background: sekme === s.key ? '#534AB7' : 'transparent',
            color: sekme === s.key ? 'white' : isDark ? '#999' : '#666',
          }}>
            {s.label}
            {s.badge > 0 &&
              <span style={{ marginLeft: 6, background: sekme === s.key ? 'rgba(255,255,255,.25)' : isDark ? '#252525' : '#eee', borderRadius: 20, padding: '1px 7px', fontSize: 11 }}>{s.badge}</span>
            }
          </button>
        ))}
      </div>

      {/* ── TAB 1: PROFİL ── */}
      {sekme === 'profil' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Profil kartı */}
          <div style={{ background: card, borderRadius: 12, padding: 24, border: `1px solid ${border}`, display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            {/* Fotoğraf */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div onClick={() => !fotoYukleniyor && document.getElementById('foto-inp')?.click()}
                style={{ cursor: 'pointer', borderRadius: '50%', overflow: 'hidden', width: 80, height: 80, position: 'relative' }}>
                {egitmen.foto_url
                  ? <img src={fotoUrl(egitmen.foto_url)} alt="" style={{ width: 80, height: 80, objectFit: 'cover' }} />
                  : <div style={{ width: 80, height: 80, background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: '#534AB7' }}>
                      {egitmen.ad?.charAt(0)}{egitmen.soyad?.charAt(0)}
                    </div>
                }
                <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: fotoYukleniyor ? 1 : 0, transition: 'opacity .2s' }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={e => !fotoYukleniyor && (e.currentTarget.style.opacity = '0')}>
                  <span style={{ color: 'white', fontSize: fotoYukleniyor ? 10 : 18 }}>{fotoYukleniyor ? '...' : '✎'}</span>
                </div>
              </div>
              <input id="foto-inp" type="file" accept="image/*" style={{ display: 'none' }} onChange={fotoYukle} />
            </div>
            {/* Bilgiler */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{egitmen.ad} {egitmen.soyad}</div>
              <div style={{ fontSize: 13, color: textSec, marginTop: 3 }}>
                {egitmen.email}{egitmen.tel ? ` · ${egitmen.tel}` : ''}
              </div>
              {(egitmen.uzmanlik ?? []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {egitmen.uzmanlik.map(u => (
                    <span key={u} style={{ background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#534AB7', borderRadius: 20, padding: '3px 10px', fontSize: 12 }}>{u}</span>
                  ))}
                </div>
              )}
              {egitmen.bio && <div style={{ fontSize: 13, color: isDark ? '#999' : '#666', marginTop: 8, lineHeight: 1.5 }}>{egitmen.bio}</div>}
            </div>
            <button onClick={duzenleAc} style={{ border: `1px solid ${isDark ? '#333' : '#ddd'}`, background: card, borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', color: '#534AB7', flexShrink: 0 }}>
              ✎ Düzenle
            </button>
          </div>

          {/* Hakedis özeti */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <HakKart baslik="Toplam kazanılan" deger={parseFloat(String(hakedis?.toplam_birikmis ?? 0))} renk="#1D9E75" bg={isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE'} isDark={isDark} />
            <HakKart baslik="Ödenen"           deger={parseFloat(String(hakedis?.toplam_odenen ?? 0))}   renk="#534AB7" bg={isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE'} isDark={isDark} />
            <HakKart baslik="Bekleyen"         deger={bekleyen}                                           renk={bekleyen > 0 ? '#854F0B' : textMuted} bg={bekleyen > 0 ? (isDark ? 'rgba(245,158,11,0.15)' : '#FAEEDA') : isDark ? '#1c1c1c' : '#f5f5f5'} isDark={isDark} />
          </div>

        </div>
      )}

      {/* ── TAB 2: DERSLERİM ── */}
      {sekme === 'dersler' && (
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
          {dersler.length === 0
            ? <Bos mesaj="Henüz atandığın bir ders yok." isDark={isDark} />
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: cardHover, borderBottom: `1px solid ${border}` }}>
                    {['Ders', 'Seviye', 'Salon', 'Seanslar', 'Hakedis', 'Kayıtlı'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 16px', fontSize: 11, color: textMuted, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dersler.map((d, i) => {
                    const [sevBg, sevRenk] = SEV_RENK[d.seviye] ?? [isDark ? '#222222' : '#f0f0f0', isDark ? '#999' : '#666'];
                    const doluluk = d.kapasite > 0 ? Math.round(d.kayitli_ogrenci / d.kapasite * 100) : 0;
                    return (
                      <tr key={d.id}
                        onClick={() => setSecilenDersId(d.id)}
                        style={{ borderBottom: i < dersler.length - 1 ? `1px solid ${isDark ? '#1c1c1c' : '#f5f5f5'}` : 'none', cursor: 'pointer' }}
                        onMouseEnter={e => (e.currentTarget.style.background = cardHover)}
                        onMouseLeave={e => (e.currentTarget.style.background = card)}
                      >
                        <td style={{ padding: '10px 16px', fontWeight: 500 }}>{d.ad}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ background: sevBg, color: sevRenk, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{d.seviye}</span>
                        </td>
                        <td style={{ padding: '10px 16px', color: isDark ? '#999' : '#666', fontSize: 12 }}>{d.salon_adi || '—'}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {(d.seanslar ?? []).map((s, si) => (
                              <span key={si} style={{ background: isDark ? '#222222' : '#f0f0f0', color: isDark ? '#bbb' : '#555', borderRadius: 20, padding: '1px 7px', fontSize: 11 }}>
                                {s.gun} {SAAT(s.baslangic_saat)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#534AB7', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>%{d.hakedis_oran}</span>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12, color: isDark ? '#bbb' : '#555' }}>{d.kayitli_ogrenci}/{d.kapasite}</span>
                            <div style={{ width: 40, background: isDark ? '#333' : '#e8e8e8', borderRadius: 3, height: 4 }}>
                              <div style={{ height: '100%', borderRadius: 3, background: doluluk >= 90 ? '#e24b4a' : doluluk >= 70 ? '#F5A623' : '#1D9E75', width: `${doluluk}%` }} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          }
        </div>
      )}

      {/* ── TAB 3: PROGRAM ── */}
      {sekme === 'program' && (() => {
        type PSeans = { dersAd: string; salon: string; bas: string; bit: string; bg: string; fg: string; };
        const gunMap: Record<string, PSeans[]> = {};
        GUNLER.forEach(g => { gunMap[g.kisalt] = []; });
        dersler.forEach((d, di) => {
          const [bg, fg] = RENK_PALETI[di % RENK_PALETI.length];
          (d.seanslar ?? []).forEach(s => {
            if (gunMap[s.gun]) gunMap[s.gun].push({ dersAd: d.ad, salon: d.salon_adi, bas: s.baslangic_saat, bit: s.bitis_saat, bg, fg });
          });
        });
        const seansToplam: PSeans[] = [];
        dersler.forEach((d, di) => {
          const [bg, fg] = RENK_PALETI[di % RENK_PALETI.length];
          (d.seanslar ?? []).forEach(s => seansToplam.push({ dersAd: d.ad, salon: d.salon_adi, bas: s.baslangic_saat, bit: s.bitis_saat, bg, fg }));
        });
        if (seansToplam.length === 0) return <Bos mesaj="Henüz atandığın bir ders yok." isDark={isDark} />;
        const minMin = Math.max(GRID_BAS, Math.min(...seansToplam.map(s => saatToMin(s.bas))) - 30);
        const maxMin = Math.min(GRID_BIT, Math.max(...seansToplam.map(s => saatToMin(s.bit))) + 30);
        const gridSure = maxMin - minMin;
        const gridYuk = Math.ceil(gridSure / 60) * SAAT_YUK;
        const saatler: number[] = [];
        for (let h = Math.floor(minMin / 60); h <= Math.ceil(maxMin / 60); h++) saatler.push(h);
        const bugunKisalt = GUNLER.find(g => g.js === new Date().getDay())?.kisalt;

        return (
          <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `48px repeat(7, 1fr)`, borderBottom: `1px solid ${border}` }}>
              <div />
              {GUNLER.map(g => (
                <div key={g.kisalt} style={{ padding: '8px 4px', textAlign: 'center', fontSize: 12, fontWeight: 600, borderLeft: `1px solid ${borderSub}`, background: g.kisalt === bugunKisalt ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : cardHover, color: gunMap[g.kisalt].length > 0 ? (g.kisalt === bugunKisalt ? '#534AB7' : isDark ? '#ccc' : '#444') : isDark ? '#444' : '#ccc' }}>
                  {g.kisalt}
                  {g.kisalt === bugunKisalt && <div style={{ fontSize: 9, color: '#534AB7', marginTop: 1 }}>bugün</div>}
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `48px repeat(7, 1fr)` }}>
              <div style={{ position: 'relative', height: gridYuk }}>
                {saatler.map(h => (
                  <div key={h} style={{ position: 'absolute', top: (h * 60 - minMin) / gridSure * gridYuk - 7, left: 0, width: '100%', fontSize: 10, color: textMuted, textAlign: 'right', paddingRight: 6 }}>
                    {String(h).padStart(2, '0')}:00
                  </div>
                ))}
              </div>
              {GUNLER.map(g => (
                <div key={g.kisalt} style={{ position: 'relative', height: gridYuk, borderLeft: `1px solid ${borderSub}`, background: gunMap[g.kisalt].length === 0 ? cardHover : card }}>
                  {saatler.map(h => (
                    <div key={h} style={{ position: 'absolute', top: (h * 60 - minMin) / gridSure * gridYuk, left: 0, right: 0, borderTop: `1px solid ${isDark ? '#1c1c1c' : '#f5f5f5'}` }} />
                  ))}
                  {gunMap[g.kisalt].map((s, si) => {
                    const top = (saatToMin(s.bas) - minMin) / gridSure * gridYuk;
                    const height = Math.max(28, (saatToMin(s.bit) - saatToMin(s.bas)) / gridSure * gridYuk - 2);
                    return (
                      <div key={si} onClick={() => { const d = dersler.find(x => x.ad === s.dersAd); if (d) setSecilenDersId(d.id); }} style={{ position: 'absolute', top, left: 3, right: 3, height, background: s.bg, borderLeft: `3px solid ${s.fg}`, borderRadius: 5, padding: '3px 6px', overflow: 'hidden', cursor: 'pointer' }}
                        onMouseEnter={e => (e.currentTarget.style.opacity = '.85')}
                        onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                      >
                        <div style={{ fontSize: 11, fontWeight: 600, color: s.fg, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.dersAd}</div>
                        <div style={{ fontSize: 10, color: s.fg, opacity: .75 }}>{SAAT(s.bas)}–{SAAT(s.bit)}</div>
                        {s.salon && <div style={{ fontSize: 10, color: s.fg, opacity: .6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.salon}</div>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── TAB 3: YOKLAMA ── */}
      {sekme === 'yoklama' && (
        <YoklamaTab dersler={dersler} egitmenId={id} />
      )}

      {/* ── Ders Detay Drawer ── */}
      {secilenDersId && (
        <>
          <div onClick={() => setSecilenDersId(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.25)', zIndex: 150 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, background: card, zIndex: 151, boxShadow: '-4px 0 24px rgba(0,0,0,.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <button onClick={() => setSecilenDersId(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: textSec, lineHeight: 1, padding: 0 }}>×</button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{dersDetay?.ders?.ad ?? '...'}</div>
                {dersDetay?.ders?.seviye && (
                  <div style={{ fontSize: 11, color: textSec, marginTop: 2 }}>{dersDetay.ders.seviye} · {dersDetay.ders.salon_adi ?? ''}</div>
                )}
              </div>
            </div>
            {dersDetayYuk
              ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: textMuted, fontSize: 13 }}>Yükleniyor...</div>
              : dersDetay && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                    <div style={{ background: cardHover, borderRadius: 8, padding: '8px 12px', border: `1px solid ${borderSub}` }}>
                      <div style={{ fontSize: 10, color: textMuted, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .3, marginBottom: 3 }}>Kapasite</div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{dersDetay.ogrenciler.length} / {dersDetay.ders.kapasite}</div>
                    </div>
                    <div style={{ background: cardHover, borderRadius: 8, padding: '8px 12px', border: `1px solid ${borderSub}` }}>
                      <div style={{ fontSize: 10, color: textMuted, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .3, marginBottom: 3 }}>Kayıt</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: dersDetay.ders.kayit_acik ? '#1D9E75' : '#e24b4a' }}>{dersDetay.ders.kayit_acik ? 'Açık' : 'Kapalı'}</div>
                    </div>
                  </div>
                  {dersDetay.ders.seanslar?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, color: textMuted, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .4, marginBottom: 6 }}>Seanslar</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {dersDetay.ders.seanslar.map((s: any, i: number) => (
                          <span key={i} style={{ background: isDark ? '#222222' : '#f0f0f0', color: isDark ? '#bbb' : '#555', borderRadius: 20, padding: '3px 9px', fontSize: 11 }}>
                            {s.gun} {SAAT(s.baslangic_saat)}–{SAAT(s.bitis_saat)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: textMuted, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .4, marginBottom: 8 }}>
                    Kayıtlı Öğrenciler ({dersDetay.ogrenciler.length})
                  </div>
                  {dersDetay.ogrenciler.length === 0
                    ? <div style={{ fontSize: 13, color: textMuted, textAlign: 'center', padding: '20px 0' }}>Kayıtlı öğrenci yok.</div>
                    : dersDetay.ogrenciler.map(o => (
                      <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, marginBottom: 2 }}
                        onMouseEnter={e => (e.currentTarget.style.background = isDark ? '#1c1c1c' : '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        {o.foto_url
                          ? <img src={fotoUrl(o.foto_url)} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          : <div style={{ width: 34, height: 34, borderRadius: '50%', background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#534AB7', flexShrink: 0 }}>
                              {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                            </div>
                        }
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.ad} {o.soyad}</div>
                          <div style={{ fontSize: 11, color: textMuted }}>{o.email}</div>
                        </div>
                        <span style={{ background: o.donduruldu ? isDark ? '#222222' : '#f0f0f0' : (o.kalan_hak === 0 || o.kalan_hak === null ? isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB' : isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE'), color: o.donduruldu ? textSec : (o.kalan_hak === 0 || o.kalan_hak === null ? '#A32D2D' : '#3B6D11'), borderRadius: 20, padding: '2px 7px', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                          {o.donduruldu ? 'Donduruldu' : `${o.kalan_hak ?? 0} hak`}
                        </span>
                      </div>
                    ))
                  }
                </div>
              )
            }
          </div>
        </>
      )}

      {/* ── Düzenleme Modalı ── */}
      {duzenleModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setDuzenleModal(false); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: '100%', maxWidth: 440 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Profil bilgilerini güncelle</div>
            <div style={{ marginBottom: 10 }}>
              <Label isDark={isDark}>Telefon</Label>
              <input value={duzenleForm.tel} onChange={e => setDuzenleForm(p => ({ ...p, tel: e.target.value }))}
                placeholder="+90 5xx xxx xx xx" style={inputStDynamic} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <Label isDark={isDark}>Uzmanlık alanları <span style={{ color: textMuted, fontWeight: 400 }}>(virgülle ayır)</span></Label>
              <input value={duzenleForm.uzmanlik} onChange={e => setDuzenleForm(p => ({ ...p, uzmanlik: e.target.value }))}
                placeholder="Arjantin Tangosu, Vals, Milonga" style={inputStDynamic} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <Label isDark={isDark}>Biyografi</Label>
              <textarea value={duzenleForm.bio} onChange={e => setDuzenleForm(p => ({ ...p, bio: e.target.value }))}
                rows={3} style={{ ...inputStDynamic, resize: 'vertical' as const }} />
            </div>
            {formHata && <div style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{formHata}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDuzenleModal(false)} style={btnStDynamic}>İptal</button>
              <button onClick={duzenleKaydet} disabled={kaydediliyor} style={{ ...btnStDynamic, background: '#534AB7', color: 'white', border: 'none' }}>
                {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Yoklama sekmesi (ayrı bileşen) ──────────────────────────────────────────
function YoklamaTab({ dersler, egitmenId }: { dersler: Ders[]; egitmenId: string }) {
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';

  const inputStDynamic: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: 13, boxSizing: 'border-box',
    background: card, color: isDark ? '#e8e8e8' : undefined,
  };
  const btnStDynamic: React.CSSProperties = {
    padding: '8px 16px', borderRadius: 6, border: `1px solid ${isDark ? '#333' : '#ddd'}`, background: card, cursor: 'pointer', fontSize: 13, color: isDark ? '#e8e8e8' : undefined,
  };

  const [tarih, setTarih] = useState(BUGUN);
  const [seciliDers, setSeciliDers] = useState<Ders | null>(null);
  const [ogrenciler, setOgrenciler] = useState<Ogrenci[]>([]);
  const [durumlar, setDurumlar] = useState<Record<string, 'katildi' | 'gelmedi'>>({});
  const [not, setNot] = useState('');
  const [yoklamaAlindi, setYoklamaAlindi] = useState(false);
  const [listYukleniyor, setListYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [gecmis, setGecmis] = useState<YoklamaGecmis[]>([]);
  const [gecmisAcik, setGecmisAcik] = useState<string | null>(null);
  const [hata, setHata] = useState('');

  // Seçili tarihe ait gün kısaltması
  const tarihGun = GUN_MAP[new Date(tarih + 'T12:00:00').getDay()];

  // O güne ait eğitmenin dersleri
  const gunDersleri = dersler.filter(d =>
    (d.seanslar ?? []).some(s => s.gun === tarihGun)
  );

  useEffect(() => {
    setSeciliDers(null); setOgrenciler([]); setDurumlar({}); setNot(''); setYoklamaAlindi(false);
  }, [tarih]);

  const dersSec = async (d: Ders) => {
    setSeciliDers(d); setListYukleniyor(true); setHata('');
    try {
      const res = await api.get(`/api/yoklama/ders/${d.id}/tarih/${tarih}`);
      setOgrenciler(res.data.ogrenciler);
      setYoklamaAlindi(res.data.yoklama_alindi);
      setNot(res.data.egitmen_notu ?? '');
      // önceki durumları doldur, yoksa hepsini katıldı yap
      const bas: Record<string, 'katildi' | 'gelmedi'> = {};
      res.data.ogrenciler.forEach((o: Ogrenci) => {
        bas[o.id] = (o.onceki_durum as 'katildi' | 'gelmedi') ?? 'katildi';
      });
      setDurumlar(bas);
    } catch { setHata('Öğrenci listesi yüklenemedi.'); }
    finally { setListYukleniyor(false); }

    // Geçmiş yoklamalar
    try {
      const gRes = await api.get(`/api/yoklama/gecmis/${d.id}`);
      setGecmis(gRes.data);
    } catch { /* yoksay */ }
  };

  const kaydet = async () => {
    if (!seciliDers) return;
    setKaydediliyor(true); setHata('');
    try {
      const satirlar = ogrenciler.map(o => ({
        ogrenci_id: o.id,
        durum: durumlar[o.id] ?? 'gelmedi',
        birim_fiyat_snapshot: parseFloat(String(o.birim_fiyat ?? 0)),
      }));
      const res = await api.post('/api/yoklama', {
        ders_id: seciliDers.id,
        tarih,
        egitmen_notu: not,
        satirlar,
      });
      setYoklamaAlindi(true);
      alert(`Yoklama kaydedildi. Hakedis: ₺${res.data.toplam_hakedis}`);
      // Geçmişi güncelle
      const gRes = await api.get(`/api/yoklama/gecmis/${seciliDers.id}`);
      setGecmis(gRes.data);
    } catch (e: any) { setHata(e.response?.data?.hata ?? 'Yoklama kaydedilemedi.'); }
    finally { setKaydediliyor(false); }
  };

  const hepsiniAyarla = (durum: 'katildi' | 'gelmedi') => {
    const yeni: Record<string, 'katildi' | 'gelmedi'> = {};
    ogrenciler.forEach(o => { yeni[o.id] = durum; });
    setDurumlar(yeni);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Tarih seçici */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <Label isDark={isDark}>Tarih</Label>
          <input type="date" value={tarih} onChange={e => setTarih(e.target.value)} max={BUGUN}
            style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: 13, background: card, color: isDark ? '#e8e8e8' : undefined }} />
        </div>
        <button onClick={() => setTarih(BUGUN)} style={{ ...btnStDynamic, marginTop: 16 }}>Bugün</button>
        <div style={{ marginLeft: 'auto', fontSize: 13, color: textSec }}>
          {gunDersleri.length > 0
            ? `${gunDersleri.length} ders var — bir ders seç`
            : 'Bu gün için ders bulunamadı'}
        </div>
      </div>

      {/* Ders seçimi */}
      {gunDersleri.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {gunDersleri.map(d => (
            <button key={d.id} onClick={() => dersSec(d)} style={{
              padding: '8px 16px', borderRadius: 8, border: '1px solid', cursor: 'pointer', fontSize: 13,
              borderColor: seciliDers?.id === d.id ? '#534AB7' : isDark ? '#333' : '#ddd',
              background: seciliDers?.id === d.id ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : card,
              color: seciliDers?.id === d.id ? '#534AB7' : isDark ? '#bbb' : '#555',
              fontWeight: seciliDers?.id === d.id ? 600 : 400,
            }}>
              {d.ad}
              <span style={{ marginLeft: 6, fontSize: 11, color: textMuted }}>
                {(d.seanslar ?? []).filter(s => s.gun === tarihGun).map(s => SAAT(s.baslangic_saat)).join(', ')}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Yoklama listesi */}
      {seciliDers && (
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{seciliDers.ad}</span>
              <span style={{ fontSize: 12, color: textSec, marginLeft: 8 }}>{new Date(tarih + 'T12:00:00').toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
            </div>
            {yoklamaAlindi && <span style={{ background: isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE', color: '#3B6D11', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>✓ Yoklama alındı</span>}
          </div>

          {listYukleniyor ? (
            <div style={{ padding: 32, textAlign: 'center', color: textMuted }}>Yükleniyor...</div>
          ) : ogrenciler.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: textMuted, fontSize: 13 }}>Bu derse kayıtlı aktif öğrenci yok.</div>
          ) : (
            <>
              {/* Toplu seçim */}
              <div style={{ padding: '8px 16px', background: cardHover, borderBottom: `1px solid ${border}`, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: textSec }}>Toplu:</span>
                <button onClick={() => hepsiniAyarla('katildi')} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #1D9E75', background: card, color: '#1D9E75', cursor: 'pointer', fontSize: 12 }}>Hepsi katıldı</button>
                <button onClick={() => hepsiniAyarla('gelmedi')} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #e24b4a', background: card, color: '#e24b4a', cursor: 'pointer', fontSize: 12 }}>Hepsi gelmedi</button>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: textSec }}>
                  {Object.values(durumlar).filter(d => d === 'katildi').length}/{ogrenciler.length} katıldı
                </span>
              </div>

              {ogrenciler.map((o, i) => (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: i < ogrenciler.length - 1 ? `1px solid ${isDark ? '#1c1c1c' : '#f5f5f5'}` : 'none', background: durumlar[o.id] === 'katildi' ? 'rgba(29,158,117,.04)' : card, transition: 'background .15s' }}>
                  {o.foto_url
                    ? <img src={fotoUrl(o.foto_url)} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{ width: 32, height: 32, borderRadius: '50%', background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#534AB7', flexShrink: 0 }}>
                        {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                      </div>
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{o.ad} {o.soyad}</div>
                    <div style={{ fontSize: 11, color: textMuted }}>{o.kalan_hak ?? 0} hak kaldı</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['katildi', 'gelmedi'] as const).map(d => (
                      <button key={d} onClick={() => setDurumlar(p => ({ ...p, [o.id]: d }))} style={{
                        padding: '5px 14px', borderRadius: 20, border: '1px solid', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                        borderColor: durumlar[o.id] === d ? (d === 'katildi' ? '#1D9E75' : '#e24b4a') : isDark ? '#333' : '#ddd',
                        background: durumlar[o.id] === d ? (d === 'katildi' ? isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE' : isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB') : card,
                        color: durumlar[o.id] === d ? (d === 'katildi' ? '#085041' : '#A32D2D') : textSec,
                      }}>
                        {d === 'katildi' ? '✓ Katıldı' : '✗ Gelmedi'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Eğitmen notu */}
              <div style={{ padding: '12px 16px', borderTop: `1px solid ${border}` }}>
                <Label isDark={isDark}>Eğitmen notu <span style={{ color: textMuted, fontWeight: 400 }}>(opsiyonel, maks 500 karakter)</span></Label>
                <textarea value={not} onChange={e => setNot(e.target.value.slice(0, 500))}
                  rows={3} placeholder="Ders notları, öğrenci gözlemleri..."
                  style={{ ...inputStDynamic, resize: 'vertical' as const, marginTop: 4 }} />
                <div style={{ textAlign: 'right', fontSize: 11, color: textMuted, marginTop: 2 }}>{not.length}/500</div>
              </div>
            </>
          )}

          {hata && <div style={{ margin: '0 16px 12px', background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12 }}>{hata}</div>}

          {ogrenciler.length > 0 && (
            <div style={{ padding: '12px 16px', borderTop: `1px solid ${border}`, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={kaydet} disabled={kaydediliyor} style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: '#534AB7', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                {kaydediliyor ? 'Kaydediliyor...' : yoklamaAlindi ? 'Yoklamayı güncelle' : 'Yoklamayı kaydet'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Geçmiş yoklamalar */}
      {seciliDers && gecmis.length > 0 && (
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, fontSize: 14, fontWeight: 600 }}>
            Geçmiş yoklamalar — {seciliDers.ad}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: cardHover, borderBottom: `1px solid ${border}` }}>
                {['Tarih', 'Katıldı', 'Gelmedi', 'Hakedis', 'Not'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, color: textMuted, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gecmis.map((g, i) => (
                <React.Fragment key={g.id}>
                  <tr style={{ borderBottom: gecmisAcik === g.id ? 'none' : i < gecmis.length - 1 ? `1px solid ${isDark ? '#1c1c1c' : '#f5f5f5'}` : 'none', cursor: g.egitmen_notu ? 'pointer' : 'default' }}
                    onClick={() => g.egitmen_notu && setGecmisAcik(p => p === g.id ? null : g.id)}>
                    <td style={{ padding: '9px 14px', color: isDark ? '#bbb' : '#555' }}>{TR_TARIH(g.tarih)}</td>
                    <td style={{ padding: '9px 14px' }}><span style={{ background: isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE', color: '#3B6D11', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>{g.katildi_sayisi}</span></td>
                    <td style={{ padding: '9px 14px' }}><span style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>{g.gelmedi_sayisi}</span></td>
                    <td style={{ padding: '9px 14px', fontWeight: 500, color: '#1D9E75' }}>₺{parseFloat(String(g.toplam_hakedis)).toLocaleString('tr-TR')}</td>
                    <td style={{ padding: '9px 14px', color: g.egitmen_notu ? '#534AB7' : isDark ? '#333' : '#ddd', fontSize: 12 }}>{g.egitmen_notu ? '▼ Notu gör' : '—'}</td>
                  </tr>
                  {gecmisAcik === g.id && (
                    <tr>
                      <td colSpan={5} style={{ padding: '0 14px 12px', background: isDark ? 'rgba(83,74,183,0.05)' : '#EEEDFE20', borderBottom: i < gecmis.length - 1 ? `1px solid ${isDark ? '#1c1c1c' : '#f5f5f5'}` : 'none' }}>
                        <div style={{ background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#534AB7', lineHeight: 1.6 }}>{g.egitmen_notu}</div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Küçük yardımcı bileşenler ────────────────────────────────────────────────
function HakKart({ baslik, deger, renk, bg, isDark }: { baslik: string; deger: number; renk: string; bg: string; isDark: boolean }) {
  const card  = isDark ? '#1a1a1a' : 'white';
  const border = isDark ? '#2a2a2a' : '#eee';
  const textSec = isDark ? '#999' : '#888';
  return (
    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, color: textSec, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .5, marginBottom: 6 }}>{baslik}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: renk }}>₺{deger.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
      <div style={{ marginTop: 6 }}><span style={{ background: bg, color: renk, borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>{baslik}</span></div>
    </div>
  );
}

function Bos({ mesaj, isDark }: { mesaj: string; isDark: boolean }) {
  const card = isDark ? '#1a1a1a' : 'white';
  const border = isDark ? '#2a2a2a' : '#eee';
  return <div style={{ background: card, border: `1px dashed ${border}`, borderRadius: 10, padding: '36px 24px', textAlign: 'center', color: isDark ? '#666' : '#bbb', fontSize: 13 }}>{mesaj}</div>;
}

function Spinner() {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#aaa', fontSize: 14 }}>Yükleniyor...</div>;
}

function Label({ children, isDark }: { children: React.ReactNode; isDark: boolean }) {
  return <div style={{ fontSize: 11, color: isDark ? '#999' : '#888', marginBottom: 4, fontWeight: 500 }}>{children}</div>;
}

const inputSt: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box',
};
const btnSt: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 13,
};
